import { ERROR_TYPE, ErrorPage, useOrganization } from '@innovamat/ga-features';
import { Modal } from '@innovamat/glimmer-components';
import type {
  Course,
  LastYearReportQuery,
  ProgressReports,
} from '@innovamat/glow-api-client';
import {
  useLastYearReportQuery,
  useProgressReportsQuery,
} from '@innovamat/glow-api-client';
import type { Stage } from '@innovamat/radiance-utils';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { StudentDrawer } from '../../components/student-drawer/student-drawer';
import useDownloadReports from '../../hooks/use-download-reports';
import { useStudentDrawer } from '../../hooks/use-student-drawer';
import { isNullOrUndefined } from '../../utils/common';
import { getColumnsForStudent } from '../../utils/get-columns-for-student';
import {
  getProgressStudentsRows,
  preprocessProgressReport,
} from '../../utils/preprocess';
import { renderTables } from '../../utils/render-tables';
import GeneralProgressHeader from './general-progress.header';
import { getGeneralColumns } from './utils/get-general-columns';
import {
  getOrderedBlockAverages,
  getOrderedStudentBlocks,
} from './utils/get-ordered-block-averages';

type GeneralReportsQuery = {
  progressReports: ProgressReports;
};

type Props = {
  isSecundaria: boolean;
  classroomId: string;
  setEventData: (eventType: string, eventProperties?: any) => void;
  classroomName: string;
  educationalStage: Stage;
  courses: Course[];
  standard: string | null;
  handleNavigate: (url: string, queryParams?: string) => void;
  handleNavigateToResource: (appletId: string) => void;
  isLastYearReport?: boolean;
  isPrimary: boolean;
  lastYearReportBannerFlag?: boolean;
  hasLastYearReport?: boolean | string;
  dayOfWeekNumber?: number;
};

export type ReportDate = {
  from: string;
  to: string;
};

export function GeneralProgress({
  isSecundaria,
  classroomId,
  setEventData,
  classroomName,
  educationalStage,
  courses,
  standard,
  handleNavigate,
  handleNavigateToResource,
  isLastYearReport,
  isPrimary,
  hasLastYearReport,
  dayOfWeekNumber,
}: Props): JSX.Element {
  const { t } = useTranslation();

  const [expandedColumn, setExpandedColumn] = useState<{
    [key: string]: string;
  }>({});
  const [standardStructure, setStandardStructure] = useState<string | null>(
    standard
  );

  useEffect(() => {
    handleSendEvent('general_dashboard_access');
  }, []);

  useEffect(() => {
    setStandardStructure(standard);
  }, [standard]);

  const stage = isSecundaria ? 'secondary' : 'primary';

  const [reportDate, setReportDate] = useState<ReportDate>({
    from: '',
    to: '',
  });

  const [initialDate, setInitialDate] = useState<ReportDate>({
    from: '',
    to: '',
  });

  const [fetchData, setFetchData] = useState({
    id: classroomId,
    from: '',
    to: '',
    stage,
    standardStructure: standard ?? '',
  });

  const { data, isLoading } = useProgressReportsQuery<GeneralReportsQuery>(
    {
      ...fetchData,
      standardStructure: standardStructure ?? '',
    },
    {
      enabled: !isLastYearReport && standardStructure !== null,
    }
  );

  const { data: lastYearData, isLoading: isLastYearDataLoading } =
    useLastYearReportQuery<LastYearReportQuery>(
      {
        id: classroomId,
        standardStructure:
          !isSecundaria && standardStructure ? standardStructure : '',
      },
      {
        enabled: !isSecundaria && isLastYearReport,
      }
    );

  const lastYearReportStartDate = lastYearData?.lastYearReport?.fromDate || '';
  const lastYearReportEndDate = lastYearData?.lastYearReport?.toDate || '';

  useEffect(() => {
    if (data?.progressReports) {
      const dateFrom = data.progressReports?.dateFrom || '';
      const dateTo = data.progressReports?.dateTo || '';
      setReportDate({
        from: dateFrom,
        to: dateTo,
      });
      if (initialDate.from === '' && initialDate.to === '') {
        setInitialDate({
          from: dateFrom,
          to: dateTo,
        });
      }
    }
  }, [data?.progressReports]);

  const handleSendEvent = (event: string, parameters?: {}): void => {
    setEventData(event, {
      classroom_id: classroomId,
      dashboard_tab: 'GeneralProgress',
      from: reportDate.from,
      to: reportDate.to,
      report_week: '',
      educational_stage: educationalStage,
      ...parameters,
    });
  };

  const handleDateChange = (date: Date, queryParam: 'from' | 'to') => {
    const timezoneOffset = date.getTimezoneOffset() * 60000;
    const localDate = new Date(date);
    if (queryParam === 'to') {
      const isMidnight =
        localDate.getHours() === 0 &&
        localDate.getMinutes() === 0 &&
        localDate.getSeconds() === 0;
      if (isMidnight) {
        localDate.setHours(23, 59, 59, 0);
      }
    }
    const adjustedDate = new Date(localDate.getTime() - timezoneOffset);
    const dateISO = adjustedDate.toISOString().split('.')[0];

    handleSendEvent('report_period_change', {
      [queryParam]: dateISO,
    });

    setFetchData((prevReportDate) => ({
      ...prevReportDate,
      from: prevReportDate.from === '' ? reportDate.from : prevReportDate.from,
      to: prevReportDate.to === '' ? reportDate.to : prevReportDate.to,
      [queryParam]: dateISO,
    }));

    setReportDate((prevReportDate) => ({
      ...prevReportDate,
      from: prevReportDate.from === '' ? reportDate.from : prevReportDate.from,
      to: prevReportDate.to === '' ? reportDate.to : prevReportDate.to,
      [queryParam]: dateISO,
    }));
  };

  const handleClickDataPoint = (dataPoint: {
    from: string;
    to: string;
  }): void => {
    const searchParams = `?from=${dataPoint.from}&to=${dataPoint.to}`;
    handleNavigate('/reports/weekly-results', searchParams);
  };

  const reports = isLastYearReport
    ? lastYearData?.lastYearReport?.reports
    : data?.progressReports.reports;

  const mainReport = reports?.[0];
  const adaptedReports = reports?.slice(1);

  const loading = isLastYearReport ? isLastYearDataLoading : isLoading;

  const reportsRows = useMemo(
    () =>
      [
        ...getProgressStudentsRows(mainReport).filter(
          (row) => row?.averageScore !== null
        ),
        [
          ...(adaptedReports?.map((adaptedReport: any) =>
            getProgressStudentsRows(adaptedReport).filter(
              (row) => row.averageScore !== null
            )
          ) || []),
        ].flat(),
      ].flat(),
    [mainReport, adaptedReports]
  );

  const {
    handleOpenStudentDrawer,
    handleCloseStudentDrawer,
    handleOnClickNext,
    handleOnClickPrevious,
    isNextDisabled,
    isPreviousDisabled,
    showStudentDrawer,
    studentData,
  } = useStudentDrawer({
    classroomName,
    reportsRows,
  });

  const { organization } = useOrganization();
  const isUs = organization?.region.startsWith('US');

  const mainReportData = {
    columns: getGeneralColumns({
      t,
      handleClickDataPoint,
      expandedColumn: expandedColumn[mainReport?.id || ''],
      isSecundaria,
      averageReport: mainReport?.average,
      handleSendEvent,
      handleNavigateToResource,
      handleOpenStudentDrawer,
      isLastYearReport,
      isUs,
    }),
    rows: getProgressStudentsRows(mainReport),
    id: mainReport?.id || '',
    averageRow: preprocessProgressReport(mainReport?.average),
    courseOrder: mainReport?.courseOrder,
  };

  const classroomBlockAverages = getOrderedBlockAverages({
    columns: mainReportData.columns,
    blockAverages: mainReportData.averageRow,
  });
  const hasClassroomAverages = classroomBlockAverages?.length > 0;
  const showStudentDrawerButton = hasClassroomAverages;

  const adaptedReportsData = adaptedReports?.map((adaptedReport: any) => {
    const id = adaptedReport?.id;
    const columns = getGeneralColumns({
      t,
      handleClickDataPoint,
      expandedColumn: expandedColumn[id],
      isSecundaria,
      averageReport: adaptedReport?.average,
      handleSendEvent,
      handleNavigateToResource,
      ...(showStudentDrawerButton && { handleOpenStudentDrawer }),
      isLastYearReport,
      isUs,
    });

    const rows = getProgressStudentsRows(adaptedReport);
    const averageRow = preprocessProgressReport(adaptedReport?.average);
    const courseOrder = adaptedReport?.courseOrder;

    return { columns, rows, id, averageRow, courseOrder };
  });

  const studentColumns = getColumnsForStudent({
    student: studentData ?? null,
    mainReportData,
    mainReport,
    adaptedReportsData,
  });

  const orderedStudentBlocks = getOrderedStudentBlocks({
    columns: studentColumns,
    studentBlocks: studentData?.blocks || [],
  });
  const studentDrawerData = {
    ...studentData,
    blocks: orderedStudentBlocks,
  };

  const onExpandColumn = (column: string, tableId: string): void => {
    setExpandedColumn((prevState) => ({
      ...prevState,
      [tableId]: column,
    }));
    handleSendEvent(column ? 'expand_column' : 'contract_column', {
      column_expanded: column,
    });
  };

  const {
    handleDownload,
    googleDriveLoading,
    openGoogleDriveFile,
    googleDriveUrl,
    onCloseModalGoogleDriveModal,
  } = useDownloadReports({
    educationalStage,
    classroomName,
    studentsRows: mainReportData.rows,
    averageRow: mainReportData.averageRow,
    adaptedReportsData,
    reportDate,
    handleSendEvent,
    reportType: 'general',
    courses,
  });

  const checkReportHasData = (students: any) => {
    return students?.some(
      (report: any) => !isNullOrUndefined(report.averageScore)
    );
  };

  function getReportDate(
    isLastYearReport?: boolean,
    lastYearDate?: string | Date,
    currentYearDate?: string | Date
  ): Date | null {
    const date = isLastYearReport ? lastYearDate : currentYearDate;
    return date ? new Date(date) : null;
  }

  const handleChangeStandard = (value: string): void => {
    setStandardStructure(value);
    setFetchData((prev) => ({
      ...prev,
      standardStructure: value ?? '',
    }));
  };

  if (!lastYearData && isLastYearReport && isSecundaria) {
    return <ErrorPage errorType={ERROR_TYPE.NOT_FOUND} />;
  }

  return (
    <>
      <GeneralProgressHeader
        loadingTable={loading}
        reportAvailable={!!mainReportData.rows.length}
        reportsStartDate={getReportDate(
          isLastYearReport,
          lastYearReportStartDate,
          initialDate.from
        )}
        reportsEndDate={getReportDate(
          isLastYearReport,
          lastYearReportEndDate,
          initialDate.to
        )}
        reportsDate={reportDate}
        handleDateChange={handleDateChange}
        handleDownload={handleDownload}
        googleDriveLoading={googleDriveLoading}
        isLastYearReport={isLastYearReport}
        standardStructure={standardStructure || ''}
        handleChangeStandard={handleChangeStandard}
        isPrimary={isPrimary}
        hasLastYearReport={hasLastYearReport}
        dayOfWeekNumber={dayOfWeekNumber}
      />
      {renderTables({
        loading,
        checkReportHasData,
        mainReportData,
        adaptedReportsData,
        allRows: reportsRows,
        handleSendEvent,
        onExpandColumn,
        t,
        courses,
        reportType: 'general',
      })}
      <Modal
        isOpen={!!googleDriveUrl}
        onClose={onCloseModalGoogleDriveModal}
        title={t('modal.googleDrive.title')}
        closeOnEsc
        closeOnClickOutside
        modalWidth={700}
        buttons={[
          {
            children: t('modal.googleDrive.button.open'),
            variant: 'accent',
            onClick: openGoogleDriveFile,
          },
          {
            children: t('common.cancel'),
            variant: 'tertiary',
            onClick: onCloseModalGoogleDriveModal,
          },
        ]}
      >
        <>{t('modal.googleDrive.content.description')}</>
      </Modal>
      <StudentDrawer
        classroomBlockAverages={classroomBlockAverages}
        classroomName={classroomName}
        isNextDisabled={isNextDisabled}
        isOpen={showStudentDrawer}
        isPreviousDisabled={isPreviousDisabled}
        student={studentDrawerData}
        onClickNext={handleOnClickNext}
        onClickPrevious={handleOnClickPrevious}
        onClose={handleCloseStudentDrawer}
      />
    </>
  );
}
